import React, { useState, useEffect } from 'react';
import { Eye, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { useToast } from '../components/ui/Toast';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/Table';
import { usePlaybooksApi, PlaybookVersion } from '../apis/playbooks-apis';
import VersionSnapshotDialog from './VersionSnapshotDialog';
import RollbackConfirmDialog from './RollbackConfirmDialog';

interface VersionHistoryPanelProps {
  playbookId: string;
  playbookName: string;
  currentVersion?: number;
  canEdit: boolean;
  onRollbackSuccess: () => void;
}

const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  playbookId,
  playbookName,
  currentVersion,
  canEdit,
  onRollbackSuccess,
}) => {
  const [versions, setVersions] = useState<PlaybookVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<number>(0);
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [rollbackTargetVersion, setRollbackTargetVersion] = useState<number>(0);
  const [isRollingBack, setIsRollingBack] = useState(false);

  const { addToast } = useToast();
  const playbooksApi = usePlaybooksApi();

  useEffect(() => {
    const fetchVersions = async () => {
      setIsLoading(true);
      try {
        const data = await playbooksApi.getVersions(playbookId);
        setVersions(data);
      } catch (error: any) {
        console.error('Error fetching versions:', error);
        addToast({
          title: "Error",
          description: error.message || 'Failed to load version history',
          variant: "destructive",
          duration: 5000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchVersions();
  }, [playbookId]);

  const handleViewClick = (version: number) => {
    setSelectedVersion(version);
    setSnapshotDialogOpen(true);
  };

  const handleRollbackClick = (version: number) => {
    setRollbackTargetVersion(version);
    setRollbackDialogOpen(true);
  };

  const handleRollbackConfirm = async () => {
    try {
      setIsRollingBack(true);
      await playbooksApi.rollbackToVersion(playbookId, rollbackTargetVersion);
      addToast({
        title: "Success",
        description: `Successfully rolled back to Version ${rollbackTargetVersion}`,
        variant: "default",
        duration: 4000,
      });
      setRollbackDialogOpen(false);
      onRollbackSuccess();

      // Refresh versions list
      const data = await playbooksApi.getVersions(playbookId);
      setVersions(data);
    } catch (error: any) {
      console.error('Error rolling back:', error);
      addToast({
        title: "Error",
        description: error.message || 'Failed to rollback to version',
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsRollingBack(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-2">
        <Spinner className="w-6 h-6 text-sky-500" />
        <p className="text-sm text-gray-600">Loading version history...</p>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <RotateCcw className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm">No versions published yet.</p>
        <p className="text-xs text-gray-400 mt-1">Publish a version to start tracking changes.</p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Version</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Author</TableHead>
              <TableHead>Note</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {versions.map((v) => {
              const isCurrent = v.version === currentVersion;
              return (
                <TableRow
                  key={v.version}
                  className={isCurrent ? 'bg-sky-50/50' : ''}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={
                          isCurrent
                            ? 'bg-gradient-to-r from-sky-500 to-cyan-500 text-white'
                            : 'bg-gray-100 text-gray-700'
                        }
                      >
                        v{v.version}
                      </Badge>
                      {isCurrent && (
                        <span className="text-xs text-sky-600 font-medium">Current</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {formatDate(v.publishedAt)}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {v.publishedByEmail || v.publishedBy}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600 max-w-[200px] truncate">
                    {v.changeNote || '-'}
                  </TableCell>
                  <TableCell>
                    {v.isRollback ? (
                      <Badge className="bg-orange-100 text-orange-700 text-xs">
                        Rollback from v{v.rolledBackFromVersion}
                      </Badge>
                    ) : (
                      <span className="text-sm text-gray-500">Publish</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewClick(v.version)}
                        className="border-sky-300 text-sky-600 hover:bg-sky-50 hover:border-sky-400"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View
                      </Button>
                      {canEdit && !isCurrent && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRollbackClick(v.version)}
                          className="border-orange-300 text-orange-600 hover:bg-orange-50 hover:border-orange-400"
                        >
                          <RotateCcw className="h-3 w-3 mr-1" />
                          Rollback
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <VersionSnapshotDialog
        open={snapshotDialogOpen}
        onOpenChange={setSnapshotDialogOpen}
        playbookId={playbookId}
        version={selectedVersion}
      />

      <RollbackConfirmDialog
        open={rollbackDialogOpen}
        onOpenChange={setRollbackDialogOpen}
        targetVersion={rollbackTargetVersion}
        playbookName={playbookName}
        onConfirm={handleRollbackConfirm}
        isRollingBack={isRollingBack}
      />
    </>
  );
};

export default VersionHistoryPanel;
